import type {
  QuestionBooleanModelData,
  QuestionCheckboxModelData,
  QuestionChoiceType,
  QuestionDropdownModelData,
  QuestionImagepickerModelData,
  QuestionRadiogroupModelData,
  QuestionRatingModelData,
  QuestionSubModelNoneAndOtherSelectable,
  QuestionType,
} from '@coorum-research/shared-common/types'
import { mapRatingArray } from '@coorum-research/shared-common/shared'

type SelectBaseOption<T extends QuestionType> = {
  type: T
  label: string
}

export type SelectBooleanOption = SelectBaseOption<'boolean'> & {
  value: boolean
}

export type SelectRatingOption = SelectBaseOption<'rating'> & {
  value: number
}

export type SelectRadiogroupOption = SelectBaseOption<'radiogroup'> & {
  value: string | 'other' | 'none'
}
export type SelectCheckboxOption = SelectBaseOption<'checkbox'> & {
  value: string | 'other' | 'none'
}

export type SelectDropdownOption = SelectBaseOption<'dropdown'> & {
  value: string | 'other' | 'none'
}

export type SelectImagepickerOption = SelectBaseOption<'imagepicker'> & {
  value: string
  imageLink: string
}

export type LogicSelectOption
  = | SelectBooleanOption
    | SelectRadiogroupOption
    | SelectRatingOption
    | SelectCheckboxOption
    | SelectDropdownOption
    | SelectImagepickerOption

export function generateBooleanSelectOption(element: QuestionBooleanModelData): SelectBooleanOption[] {
  if (element.swapOrder === true) {
    return [
      {
        type: element.type,
        label: element.labelTrue ?? 'はい',
        value: true,
      },
      {
        type: element.type,
        label: element.labelFalse ?? 'いいえ',
        value: false,
      },
    ]
  }

  return [
    {
      type: element.type,
      label: element.labelFalse ?? 'いいえ',
      value: false,
    },
    {
      type: element.type,
      label: element.labelTrue ?? 'はい',
      value: true,
    },
  ]
}

/**
 * - rateCount
 * - rateMax
 * - rateValues
 * を利用
 * rateValuesがある場合には、その値を利用する
 * rateValuesが無い場合には、rateCount, rateMaxを利用して1~rateMaxまでの値を生成する
 */
export function generateRatingSelectOption(element: QuestionRatingModelData): SelectRatingOption[] {
  if (element.rateValues) {
    return element.rateValues.map((value) => {
      if (typeof value === 'number') {
        return {
          type: element.type,
          label: String(value),
          value,
        }
      } else {
        return {
          type: element.type,
          label: value.text,
          value: value.value,
        }
      }
    })
  } else {
    return mapRatingArray({
      rateCount: element.rateCount,
      rateMin: element.rateMin,
      rateStep: element.rateStep,
    }, (value) => {
      return {
        type: element.type,
        label: String(value),
        value,
      }
    })
  }
}

/**
 * 選択肢を生成する共通関数
 */
export function generateChoiceSelectOption<
  Ele extends {
    type: QuestionType
    choices: QuestionChoiceType[]
  } & QuestionSubModelNoneAndOtherSelectable,
  T extends Ele['type'],
  C extends Ele['choices'][number],
  Ret extends {
    type: T
    label: string
    value: any
  },
>(
  element: Ele,
  makeOption: (choice: C, type: T) => Ret,
): Ret[] {
  const allOptions: Ret[] = []
  const choiceOptions = element.choices.map((choice) => {
    return makeOption(choice as C, element.type as T)
  })
  allOptions.push(...choiceOptions)

  if (element.showNoneItem) {
    allOptions.push({
      type: element.type,
      label: element.noneText ?? 'なし',
      value: 'none',
    } as Ret)
  }

  if (element.showOtherItem) {
    allOptions.push({
      type: element.type,
      label: element.otherText ?? 'その他（説明）',
      value: 'other',
    } as Ret)
  }

  return allOptions
}

/**
 * radiogroupの選択肢を生成する
 */
export function generateRadiogroupSelectOption(element: QuestionRadiogroupModelData): SelectRadiogroupOption[] {
  return generateChoiceSelectOption(element, (choice, type) => {
    if (typeof choice === 'string') {
      return {
        type,
        label: choice,
        value: choice,
      }
    } else {
      return {
        type,
        label: choice.text,
        value: choice.value,
      }
    }
  })
}

/**
 * checkboxの選択肢を生成する
 */
export function generateCheckboxSelectOption(element: QuestionCheckboxModelData): SelectCheckboxOption[] {
  return generateChoiceSelectOption(element, (choice, type) => {
    if (typeof choice === 'string') {
      return {
        type,
        label: choice,
        value: choice,
      }
    } else {
      return {
        type,
        label: choice.text,
        value: choice.value,
      }
    }
  })
}

/**
 * dropdownの選択肢を生成する
 */
export function generateDropdownSelectOption(element: QuestionDropdownModelData): SelectDropdownOption[] {
  return generateChoiceSelectOption(element, (choice, type) => {
    if (typeof choice === 'string') {
      return {
        type,
        label: choice,
        value: choice,
      }
    } else {
      return {
        type,
        label: choice.text,
        value: choice.value,
      }
    }
  })
}

/**
 * imagepickerの選択肢を生成する
 */
export function generateImagepickerSelectOption(element: QuestionImagepickerModelData): SelectImagepickerOption[] {
  return element.choices?.map((choice) => {
    return {
      type: element.type,
      label: choice.text ?? choice.value,
      value: choice.value,
      imageLink: choice.imageLink,
    }
  }) ?? []
}

/**
 * inputは string valueになるので、適切な型に変換する
 * 特にboolean
 */
export function coerceInputValue(value: string, questionType: QuestionType | undefined): string | boolean | number {
  let coerceValue: string | boolean | number = value
  if (questionType === 'boolean') {
    if (value === 'true') {
      coerceValue = true
    } else if (value === 'false') {
      coerceValue = false
    }
  }
  if (questionType === 'rating') {
    coerceValue = Number(value)
  }
  return coerceValue
}
